"use client";

import { c as _c } from "react/compiler-runtime";
import { SearchIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataTableFilter } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
type FilterState = {
  search: string;
  documentType: string;
  location: string[];
  status: string[];
  page: string;
};
type FilterUpdate = Partial<Record<keyof FilterState, string | string[]>>;
interface ProducerFiltersProps {
  /** True when any search or facet narrows the list; shows "Limpar". */
  hasActiveFilters: boolean;
  /** True while the typed search waits for its debounce. */
  isSearching: boolean;
  uniqueLocations: string[];
  updateSearchParams: (updates: FilterUpdate) => void;
  searchParams: FilterState;
}
const DOCUMENT_TYPE_OPTIONS = [{
  label: "Pessoa física",
  value: "cpf"
}, {
  label: "Empresa",
  value: "cnpj"
}];
const STATUS_OPTIONS = [{
  label: "Ativo",
  value: "active"
}, {
  label: "Inativo",
  value: "inactive"
}];

/**
 * Producers list toolbar: one search box plus facet buttons that show their own selection,
 * so there is no separate chip row.
 */
export const ProducerFilters = memo(function ProducerFiltersImpl(t0) {
  const $ = _c(45);
  const {
    hasActiveFilters,
    isSearching,
    uniqueLocations,
    searchParams,
    updateSearchParams
  } = t0;
  let t1;
  if ($[0] !== uniqueLocations) {
    t1 = uniqueLocations.map(_temp);
    $[0] = uniqueLocations;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const locationOptions = t1;
  let t2;
  if ($[2] !== updateSearchParams) {
    t2 = event => {
      updateSearchParams({
        search: event.target.value,
        page: "1"
      });
    };
    $[2] = updateSearchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleSearchChange = t2;
  let t3;
  if ($[4] !== searchParams.documentType || $[5] !== updateSearchParams) {
    t3 = values => {
      const next = values.find(value => value !== searchParams.documentType) ?? "";
      updateSearchParams({
        documentType: next,
        page: "1"
      });
    };
    $[4] = searchParams.documentType;
    $[5] = updateSearchParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleDocumentTypeChange = t3;
  let t4;
  if ($[7] !== updateSearchParams) {
    t4 = () => {
      updateSearchParams({
        search: "",
        documentType: "",
        location: [],
        status: [],
        page: "1"
      });
    };
    $[7] = updateSearchParams;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const clearFilters = t4;
  const t5 = isSearching && "pr-18";
  let t6;
  if ($[9] !== t5) {
    t6 = cn(t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== handleSearchChange || $[12] !== searchParams.search || $[13] !== t6) {
    t7 = <Input aria-label={"Buscar por nome, raz\xE3o social ou CPF/CNPJ"} className={t6} leftIcon={SearchIcon} name="search" onChange={handleSearchChange} placeholder={"Buscar por nome ou CPF/CNPJ\u2026"} value={searchParams.search} />;
    $[11] = handleSearchChange;
    $[12] = searchParams.search;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isSearching) {
    t8 = isSearching ? <output className="text-muted-foreground pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs">Buscando…</output> : null;
    $[15] = isSearching;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="relative min-w-56 flex-1 sm:max-w-sm">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== searchParams.documentType) {
    t10 = searchParams.documentType ? [searchParams.documentType] : [];
    $[20] = searchParams.documentType;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleDocumentTypeChange || $[23] !== t10) {
    t11 = <DataTableFilter onChange={handleDocumentTypeChange} options={DOCUMENT_TYPE_OPTIONS} title="Tipo" values={t10} />;
    $[22] = handleDocumentTypeChange;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== updateSearchParams) {
    t12 = values_0 => updateSearchParams({
      location: values_0,
      page: "1"
    });
    $[25] = updateSearchParams;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== locationOptions || $[28] !== searchParams.location || $[29] !== t12) {
    t13 = <DataTableFilter onChange={t12} options={locationOptions} title={"Localiza\xE7\xE3o"} values={searchParams.location} />;
    $[27] = locationOptions;
    $[28] = searchParams.location;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== updateSearchParams) {
    t14 = values_1 => updateSearchParams({
      status: values_1,
      page: "1"
    });
    $[31] = updateSearchParams;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== searchParams.status || $[34] !== t14) {
    t15 = <DataTableFilter onChange={t14} options={STATUS_OPTIONS} title="Status" values={searchParams.status} />;
    $[33] = searchParams.status;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== clearFilters || $[37] !== hasActiveFilters) {
    t16 = hasActiveFilters ? <Button className="text-muted-foreground" onClick={clearFilters} variant="ghost">Limpar</Button> : null;
    $[36] = clearFilters;
    $[37] = hasActiveFilters;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t11 || $[40] !== t13 || $[41] !== t15 || $[42] !== t16 || $[43] !== t9) {
    t17 = <div className="flex flex-wrap items-center gap-2">{t9}{t11}{t13}{t15}{t16}</div>;
    $[39] = t11;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
    $[43] = t9;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
});
function _temp(location) {
  return {
    label: location,
    value: location
  };
}